import { Fragment, useState } from 'react'
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { AppLayout } from '@/components/AppLayout'
import { TopBarBackButton } from '@/components/topbar'
import { DesktopPage } from '@/components/desktop/DesktopPage'
import { DesktopPageHeader } from '@/components/desktop/DesktopPageHeader'
import { useIsDesktop } from '@/components/ui/use-mobile'
import { Card } from '@/components/ui/card'
import { SearchInput } from '@/components/ui/search-input'
import { DetailSkeleton } from '@/components/ui/skeleton-presets'
import { EmptyState } from '@/components/EmptyState'
import { ArrowLeft, ArrowRight, FileCheck } from '@/components/icons'
import { useArchivedTasks } from '@/lib/hooks/task'
import { OperationsTaskReviewDrawer } from '@/components/operations/OperationsTaskReviewDrawer'

export const Route = createFileRoute('/_auth/operations/tasks/archived/')({
  component: AdminArchivedTasksRoute,
})

interface ArchivedTask {
  id: string
  name: string
  status: string
  submittedAt: Date | string | null
  aircraft: { registration: string } | null
  tripLeg: {
    departureAirportIcao: string
    arrivalAirportIcao: string
    trip: { tripName: string }
  } | null
}

function AdminArchivedTasksRoute() {
  const { data: tasks, isLoading } = useArchivedTasks()
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const query = search.trim().toLowerCase()
  const filteredTasks = query
    ? (tasks as ArchivedTask[] | undefined)?.filter((t) =>
        [
          t.name,
          t.tripLeg?.trip.tripName,
          t.tripLeg?.departureAirportIcao,
          t.tripLeg?.arrivalAirportIcao,
          t.aircraft?.registration,
        ]
          .filter(Boolean)
          .some((f) => f!.toLowerCase().includes(query)),
      )
    : (tasks as ArchivedTask[] | undefined)

  const body = (
    <>
      {isLoading ? (
        <DetailSkeleton rows={5} />
      ) : !tasks?.length ? (
        <EmptyState
          icon={FileCheck}
          title="No archived tasks"
          description="Read tasks will appear here."
        />
      ) : (
        <div className="flex flex-col gap-3">
          <div className="sticky top-0 z-10 bg-surface-brand-L0 pb-2">
            <SearchInput
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClear={() => setSearch('')}
            />
          </div>
          {filteredTasks?.length ? (
            <Card className="flex flex-col gap-0 p-0 overflow-hidden">
              {filteredTasks.map((task, idx) => (
                <Fragment key={task.id}>
                  <TaskRow task={task} onClick={() => setSelectedTaskId(task.id)} />
                  {idx < filteredTasks.length - 1 && (
                    <div className="h-px bg-border-L1 mx-4" />
                  )}
                </Fragment>
              ))}
            </Card>
          ) : (
            <EmptyState
              icon={FileCheck}
              title="No matching tasks"
              description="Try a different search."
            />
          )}
        </div>
      )}

      <OperationsTaskReviewDrawer
        taskId={selectedTaskId}
        onOpenChange={(open) => {
          if (!open) setSelectedTaskId(null)
        }}
      />
    </>
  )

  const breadcrumb = (
    <span className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => navigate({ to: '/tasks' })}
        className="flex size-7 items-center justify-center rounded-full hover:bg-surface-L1"
        aria-label="Back to checklists"
      >
        <ArrowLeft className="size-4" />
      </button>
      <span>
        <Link to="/tasks" className="hover:underline">
          Checklists
        </Link>
        <span className="text-content-primary-subtle"> / Archived Tasks</span>
      </span>
    </span>
  )

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader title="Archived Tasks" breadcrumb={breadcrumb} />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout topBar={<TopBarBackButton title="Archived Tasks" />}>
      {body}
    </AppLayout>
  )
}

function TaskRow({ task, onClick }: { task: ArchivedTask; onClick: () => void }) {
  const submittedAt = task.submittedAt ? dayjs(task.submittedAt) : null

  return (
    <button
      onClick={onClick}
      className="w-full text-left flex items-start gap-3 px-4 py-3 bg-surface-L0"
    >
      <div className="flex flex-1 flex-col gap-1 min-w-0">
        <p className="text-sm font-semibold text-content-primary-bold leading-5 truncate">
          {task.name}
        </p>
        <div className="flex items-center gap-3 text-sm text-content-primary-subtle leading-5">
          {task.tripLeg ? (
            <>
              <span className="truncate">{task.tripLeg.trip.tripName}</span>
              <span className="flex items-center gap-2 shrink-0">
                <span>{task.tripLeg.departureAirportIcao}</span>
                <ArrowRight className="size-3 text-content-primary-subtle-mid" />
                <span>{task.tripLeg.arrivalAirportIcao}</span>
              </span>
            </>
          ) : (
            <span className="truncate">
              {task.aircraft ? task.aircraft.registration : 'Standalone task'}
            </span>
          )}
        </div>
        {submittedAt && (
          <p className="text-xs font-semibold text-content-primary-subtle leading-4">
            {submittedAt.format('MMM D, YYYY')}
          </p>
        )}
      </div>
    </button>
  )
}
